import React, { useEffect, useMemo, useState } from "react";

import { Dialog, DialogTitle, DialogActions, Button, CircularProgress, FormControl, FormControlLabel, Switch, makeStyles } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";

import { ShareService } from "../../services/ShareService";
import { BuilderAsset } from "../../entities/BuilderAsset";
import { BuilderAssetService } from "../../services/BuilderAssetService";
import { printAssetCategory } from "../../entities/AssetCategory";

import { NonPaidUnshareDialog } from "./NonPaidUnshareDialog";
import { useIsMobile } from "../Mui3WidthProvider";
import { useAppConfig } from "../../utilities/useAppConfig";
import { useAuthenticatedUser } from "../../utilities/useAuthenticatedUser";
import { SharedContent } from "./SharedContent";
import { UnsharedContent } from "./UnsharedContent";

const useStyles = makeStyles((theme) => ({
    container: {
      minWidth: "600px",
      [theme.breakpoints.down("sm")]: {
        minWidth: "initial",
      },
    },
    closeMobileIcon: {
      color: "rgb(0, 0, 0, .4)",
      position: "absolute",
      top: 10,
      right: 10,
    },
    titleRight: {
      float: "right",
      paddingTop: 5,
      [theme.breakpoints.down("sm")]: {
        float: "initial",
      },
    },
    mobileFormLabel: {
      [theme.breakpoints.down("sm")]: {
        marginLeft: 0,
      },
    },
    switchLabel: {
      fontWeight: 500,
      fontSize: 16,
    },
    loadingContainer: {
      height: 375,
      width: 600,
      textAlign: "center",
      [theme.breakpoints.down("sm")]: {
        height: "auto",
        width: "auto",
      },
    },
  }));

interface Props {
  onClose: () => void;
  isOpen: boolean;
  onShareChangeSuccess: (
    id: string,
    isShared: boolean,
    shareId: string
  ) => void;
  shareFromSlides?: boolean;
  id: string;
  onError: (message: string) => void;
  onGiveMessage: (message: string) => void;
}

export function ShareDialog(props:Props){
  const classes = useStyles();
  const isMobile = useIsMobile();
  const [config,] = useAppConfig();
  const user = useAuthenticatedUser();

  const [loading, setLoading] = useState(true)
  const [item, setItem] = useState<BuilderAsset>()
  const [url, setUrl] = useState("")
  const [openUnshareNonPaidDialog, setOpenUnshareNonPaidDialog] = useState(false)

  const fromSlides = useMemo(() => Boolean(props.shareFromSlides), [props.shareFromSlides])
  const title = useMemo(() => item ? fromSlides ? "Share Slides" : `Share ${printAssetCategory(item.category)}`: "", [item, fromSlides]);
  const activeMakerSubscription = useMemo(() => user.makerSubscription, [user]);

  useEffect(() => {
    async function loadAsset(){
      setLoading(true);
      let responseItem = await BuilderAssetService.get(props.id);

      if(responseItem.error){
        props.onError("Unable to load shared infomation. Please try again");
        return props.onClose();
      }

      const builderAsset = responseItem.data;
      let sharedId = builderAsset.shareId;

      if(!builderAsset.shareId || !builderAsset.isShared){
        const sharedResponse = await ShareService.share(builderAsset.id);

        if(sharedResponse.error){
          props.onError("Unable to initialize share. Please try again");
          return props.onClose();
        }
        const sharedItem = sharedResponse.data;
        sharedId = sharedItem.shareId;
        props.onShareChangeSuccess(sharedItem.id, sharedItem.isShared, sharedId!);
      }

      if(!sharedId){
        props.onError("Unable to initialize share. Please try again");
        return props.onClose();
      }

      const response = await ShareService.getSharedAsset(sharedId);
      setLoading(false);

      if(response.error){
        props.onError("Unable to load shared infomation. Please try again");
        return props.onClose();
      }

      setItem(response.data);
      setUrl(determineUrl(response.data))
    };

    loadAsset()
  }, [])

  const handleShareToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!item) {
      return;
    }

    handleShareDetermination(e.target.checked, item);
  };

  const handleShareDetermination = async (isShared: boolean, item: BuilderAsset) => {
    if (isShared)
      return await share(item.id);

    if (!activeMakerSubscription)
        return setOpenUnshareNonPaidDialog(true);

    await unshare(item.id);
  };

  const share = async (id: string) => {
    setLoading(true);
    let shareResponse = await ShareService.share(id);
    setLoading(false);

    if(shareResponse.success){
      const data = shareResponse.data;
      setItem({ ...item!, isShared: data.isShared, shareId: data.shareId })
      props.onShareChangeSuccess( data.id, data.isShared, data.shareId!)
    }
  };

  const unshare = async (id: string) => {
    setLoading(true);
    let shareResponse = await ShareService.unshare(id);
    setLoading(false);

    if(shareResponse.success){
      const data = shareResponse.data;
      setItem({ ...item!, isShared: data.isShared, shareId: data.shareId })
      props.onShareChangeSuccess( data.id, data.isShared, data.shareId!)
    }
  };

  const determineUrl = (asset: BuilderAsset) => {
    if (!asset.shareId) {
      return "";
    }

    let url = `${config.makerSiteUrl}/share/`;

    if (props.shareFromSlides && props.shareFromSlides === true) {
      url += "slide/";
    }

    url += asset.shareId;

    return url;
  }

  const handleCopy = () => {
    let el = document.getElementById("urlInput") as HTMLInputElement;

    if (!el) {
      return;
    }

    // handle iOS as a special case
    if (navigator.userAgent.match(/ipad|ipod|iphone/i)) {
      // save current contentEditable/readOnly status
      var editable = el.contentEditable;
      var readOnly = el.readOnly;

      // convert to editable with readonly to stop iOS keyboard opening
      el.contentEditable = "true";
      el.readOnly = true;

      // create a selectable range
      var range = document.createRange();
      range.selectNodeContents(el);

      // select the range
      var selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
      el.setSelectionRange(0, 999999);

      // restore contentEditable/readOnly to original state
      el.contentEditable = editable;
      el.readOnly = readOnly;
    } else {
      el.select();
    }

    document.execCommand("copy");
    props.onGiveMessage("Link copied to clipboard.");
  }

  if (openUnshareNonPaidDialog && item) {
    return <NonPaidUnshareDialog open={openUnshareNonPaidDialog} onClose={props.onClose} item={item} onShareChangeSuccess={props.onShareChangeSuccess} />
  }

  return (
    <Dialog
      open={props.isOpen}
      onClose={() => props.onClose()}
      fullScreen={isMobile}
    >
      <div className={classes.container}>
        {loading && (
          <div className={classes.loadingContainer}>
            <CircularProgress style={{ marginTop: "150px" }} />
          </div>
        )}
        {item && !loading && (
          <>
            {isMobile && (
              <CloseIcon
                className={classes.closeMobileIcon}
                onClick={props.onClose}
              />
            )}
            <DialogTitle id="form-dialog-title">
              {title}
              <div className={classes.titleRight}>
                <FormControl>
                  <FormControlLabel label={`Sharing is ${item.isShared ? "on" : "off"}`}
                    className={classes.mobileFormLabel}
                    control={
                      <Switch
                        checked={item.isShared}
                        onChange={handleShareToggle}
                        value="Share"
                        color="primary"
                      />
                    }
                    classes={{ label: classes.switchLabel }}
                    labelPlacement="start"
                  />
                </FormControl>
              </div>
            </DialogTitle>
            {item.isShared 
              ? <SharedContent item={item} fromSlides={fromSlides} url={url} handleCopy={handleCopy} />
              : <UnsharedContent url={url} handleCopy={handleCopy} />
            }
            <DialogActions>
              <Button onClick={props.onClose} color="primary">
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </div>
    </Dialog>
  );
}